import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_COLOR_BLIND,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import { findColorBlingHighestPercentageTrait } from "../../utils/assessment-utils";

import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import { useTranslation } from "react-i18next";
import {
  getEnneagramBannerImage,
  getColirBlindnessImage,
  toCamelCase,
} from "../../utils/utility-functions";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import Footer from "../../components/footer";
import ExpandableSection from "../../components/expandable-collapsible/component";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import { CallToActionSection } from "../../components/call-to-action/component";
import InviteFriend from "../../components/invite-friend";
import PathHelper from "../../utils/path-helper";
import ReportWrapper from "../../components/report-wrapper";

const ColorBlindReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,

  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const navigate = useNavigate();

  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const { t } = useTranslation();
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  const colorblindness1 = "color-blindness-report-1";
  const colorblindness2 = "color-blindness-report-2";
  const colorblindness3 = "color-blindness-report-3";
  const colorblindnessImage1 = getColirBlindnessImage(colorblindness1);
  const colorblindnessImage2 = getColirBlindnessImage(colorblindness2);
  const colorblindnessImage3 = getColirBlindnessImage(
    colorblindness3,
    currentLocale
  );
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_COLOR_BLIND[currentLocale] ||
            MODEL_TYPE_COLOR_BLIND[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;

          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId, currentLocale]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  const bannerImage = getEnneagramBannerImage();

  // Get the highest percentage trait
  const highestTrait = findColorBlingHighestPercentageTrait(userTraits);
  const traitKey = highestTrait?.trait?.title
    .toLowerCase()
    .replaceAll("_", "-");

  const selectedResult =
    traitKey && contentData?.results?.[toCamelCase(traitKey)]
      ? contentData.results[toCamelCase(traitKey)]
      : contentData?.results?.notColorBlind; // Convert keys to lowercase

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };
  const HeroBanner = ({
    title,
    color = "purple",
    responsiveNoWrap = false,
  }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    if (!title) return null;

    const isString = typeof title === "string";
    let firstWord = "";
    let restOfTitle = "";
    if (isString) {
      const words = title.split(" ");
      firstWord = words[0];
      restOfTitle = words.slice(1).join(" ");
    }

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent z-5`}
        />
        <div className="absolute inset-0 bg-black opacity-10 z-5" />

        <div className="relative h-full flex items-start" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12" id={reportType}>
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start pr-10 md:pr-16">
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className={`text-3xl font-bold text-white text-left break-words ${
                  responsiveNoWrap
                    ? "whitespace-nowrap sm:whitespace-normal"
                    : "whitespace-normal"
                }`}
              >
                {isString ? (
                  <span className="inline-block max-w-full md:max-w-none leading-tight">
                    <span>{firstWord}</span> <span>{restOfTitle}</span>
                  </span>
                ) : (
                  title
                )}
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSectionForUnAuth = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.colorblindness.personality_test_cta")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.discover_more_about_your_personality_type")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id="color-blind-take-personality-test-btn"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("dashboard.take_test_cta")}
            </button>
          </div>
        </div>
      </section>
    );
  };
  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]"></div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const InfoListCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    className = "",
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full ${className}`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4 pl-6 md:pl-10">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
        style={{ filter: "drop-shadow(0 0 5px rgba(0,0,0,0.3))" }}
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          stroke="none"
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-4 items-start">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal">
                          {t("report.colorblindness.introduction")}
                        </h2>
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                          {contentData.intro}
                        </p>
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                          <strong>
                            {t("report.colorblindness.important")}
                          </strong>{" "}
                          {contentData?.firstNotice
                            ?.replace(t("report.colorblindness.important"), "")
                            .trim()}
                        </p>
                      </div>
                      <div className="flex items-cente items-end ">
                        <img
                          src={colorblindnessImage1}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain "
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={selectedResult?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                      {selectedResult?.FlavorText}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 items-center">
                    <div className="w-full p-4 md:px-8 md:py-6 ">
                      <InfoListCard
                        heading={selectedResult?.Content?.heading}
                        bgClr="#ffe0e0"
                        character="i"
                        contentArray={selectedResult?.Content?.Content}
                      />
                      <div className="mt-6">
                        <DescriptiveCard
                          overview={selectedResult?.reassurance}
                          bgClr={"bg-yellow-50"}
                          textClr={"text-yellow-800"}
                        />
                      </div>
                    </div>
                    <div className="flex items-end">
                      <img
                        src={colorblindnessImage2}
                        alt="Banner Image"
                        className="w-full object-contain "
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.understanding?.RootHeading}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6 space-y-6">
                    {/* Heading */}
                    <div>
                      <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal">
                        {contentData?.understanding?.types?.Heading}
                      </h2>
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                        {contentData?.understanding?.types?.FlavorText}
                      </p>
                    </div>

                    {/* Grid Section */}
                    <div className="grid grid-cols-1 md:grid-cols-[7fr_3fr] gap-8 md:gap-12 items-start">
                      {/* Left Column */}
                      <div className="space-y-6">
                        {/* Red-Green Section */}
                        <InfoListCard
                          heading={
                            contentData?.understanding?.types?.["Red-green"]
                              ?.Heading
                          }
                          overview={
                            contentData?.understanding?.types?.["Red-green"]
                              ?.FlavorText
                          }
                          bgClr="#ffe0e0"
                          character="i"
                          contentArray={
                            contentData?.understanding?.types?.["Red-green"]
                              ?.Bullets
                          }
                        />

                        {/* Blue-Yellow Section */}
                        {/* Desktop only */}
                        <InfoListCard
                          heading={
                            contentData?.understanding?.types?.["Red-green"]?.[
                              "Blue-yellow"
                            ]?.Heading
                          }
                          overview={
                            contentData?.understanding?.types?.["Red-green"]?.[
                              "Blue-yellow"
                            ]?.FlavorText
                          }
                          bgClr="#e0f7fa"
                          character="i"
                          contentArray={
                            contentData?.understanding?.types?.["Red-green"]?.[
                              "Blue-yellow"
                            ]?.Bullets
                          }
                          className="hidden lg:block"
                        />

                        {/* Mobile only */}
                        <div className="md:hidden">
                          <InfoListCard
                            heading={
                              contentData?.understanding?.types?.[
                                "Red-green"
                              ]?.["Blue-yellow"]?.Heading
                            }
                            overview={
                              contentData?.understanding?.types?.[
                                "Red-green"
                              ]?.["Blue-yellow"]?.FlavorText
                            }
                            bgClr="#e0f7fa"
                            character="i"
                            contentArray={
                              contentData?.understanding?.types?.[
                                "Red-green"
                              ]?.["Blue-yellow"]?.Bullets
                            }
                          />
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="flex flex-col items-center md:justify-center space-y-6">
                        {/* Image */}
                        <img
                          src={colorblindnessImage3}
                          alt="Banner Image"
                          className="w-full max-h-[300px] md:max-h-[400px] lg:max-h-[500px] object-contain"
                        />

                        {/* Blue-Yellow for tablet only */}
                        <div className="hidden md:flex lg:hidden w-full">
                          <InfoListCard
                            heading={
                              contentData?.understanding?.types?.[
                                "Red-green"
                              ]?.["Blue-yellow"]?.Heading
                            }
                            overview={
                              contentData?.understanding?.types?.[
                                "Red-green"
                              ]?.["Blue-yellow"]?.FlavorText
                            }
                            bgClr="#e0f7fa"
                            character="i"
                            contentArray={
                              contentData?.understanding?.types?.[
                                "Red-green"
                              ]?.["Blue-yellow"]?.Bullets
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Everyday Impact */}
                    <div>
                      <InfoListCard
                        character={"i"}
                        heading={
                          contentData?.understanding?.everydayImpact?.Heading
                        }
                        contentArray={
                          contentData?.understanding?.everydayImpact?.Bullets
                        }
                        bgClr={"#e3fbeeff"}
                      />
                    </div>

                    {/* Living With */}
                    <div>
                      <DescriptiveCard
                        heading={
                          contentData?.understanding?.livingWith?.Heading
                        }
                        overview={
                          contentData?.understanding?.livingWith?.Content
                        }
                        bgClr={"bg-yellow-50"}
                      />
                    </div>

                    {/* What Can I Do */}
                    <div>
                      <InfoListCard
                        character={"i"}
                        heading={
                          contentData?.understanding?.whatCanIDo?.Heading
                        }
                        overview={
                          contentData?.understanding?.whatCanIDo?.FlavorText
                        }
                        contentArray={
                          contentData?.understanding?.whatCanIDo?.Bullets
                        }
                        bgClr={"#FFE3E8"}
                      />
                    </div>

                    {/* When to See Doctor */}
                    <div>
                      <DescriptiveCard
                        heading={
                          contentData?.understanding?.whenToSeeDoctor?.Heading
                        }
                        overview={
                          contentData?.understanding?.whenToSeeDoctor?.Content
                        }
                        bgClr={"bg-orange-50"}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.nextSteps?.RootHeading}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p
                      className={`"text-gray-700 text-base md:text-lg leading-relaxed mb-4" `}
                    >
                      {contentData?.nextSteps?.Content}
                    </p>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.finalNotice?.RootHeading}
                      color="yellow"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p
                      className={`"text-gray-700 text-base md:text-lg leading-relaxed mb-4"`}
                    >
                      {contentData?.finalNotice?.Content}
                    </p>
                  </div>
                </ExpandableSection>
              </section>
              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                <CallToActionSectionForUnAuth data={contentData} />
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getTransactionDetailConnect={getTransactionDetailConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
          <ReportWrapper
            assessmentId={assessmentId}
            assessmentAttemptId={assessmentAttemptId}
            currentLocale={currentLocale}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ColorBlindReport;
